import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Avatar,
  Divider,
  IconButton,
  LinearProgress,
  Button,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import api from "../../../../lib/ghost";

import { Helmet } from "react-helmet-async";
import Seo from "../../../../seo/Seo";

type GhostPost = {
  id: string;
  title: string;
  slug: string;
  html: string;
  updated_at: string;
  feature_image?: string;
  published_at: string;
  primary_author?: { name: string; profile_image?: string | null } | null;
  tags?: { name: string; slug: string }[];
};

function estimateReadingTime(html = "") {
  const text = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text ? text.split(" ").length : 0;
  const minutes = Math.max(1, Math.round(words / 200));
  return { words, minutes };
}

function useReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      const scrolled = total > 0 ? (h.scrollTop / total) * 100 : 0;
      setProgress(scrolled);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [post, setPost] = useState<GhostPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        const data: any = await api.posts.read(
          { slug: slug as string },
          { include: ["tags", "authors"] }
        );
        if (alive) setPost(data);
      } catch (e: any) {
        setErr(e?.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (slug) load();
    return () => {
      alive = false;
    };
  }, [slug]);

  const reading = useMemo(
    () => estimateReadingTime(post?.html || ""),
    [post?.html]
  );
  const progress = useReadingProgress();
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const overlay = `linear-gradient(
    190deg,
    ${alpha(theme.palette.primary.main, 0.55)} 0%,
    ${alpha(theme.palette.primary.light, 0.35)} 50%,
    ${alpha(theme.palette.primary.dark, 0.3)} 100%
  )`;

  if (!post) return null;

  const SITE = "https://www.bondpay.au/";

  const urlPath = `/article/${post.slug}`;
  const desc = post.html
    .replace(/<[^>]+>/g, " ")
    .trim()
    .slice(0, 160);
  const img = post.feature_image;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: img ? [img] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: post.primary_author?.name
      ? { "@type": "Person", name: post.primary_author.name }
      : undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}${urlPath}` },
    description: desc,
  };
  return (
    <>
      <Seo
        title={`${post.title} | Blog`}
        description={desc}
        path={urlPath}
        image={img}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      </Helmet>

      <Box sx={{ bgcolor: "background.default", color: "text.primary" }}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 2000,
          }}
        >
          <LinearProgress variant="determinate" value={progress} />
        </Box>

        <Box
          sx={{
            position: "relative",
            height: { xs: "72vh", md: "92vh" },
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            overflow: "hidden",
            bgcolor: "black",
            borderBottomLeftRadius: { md: 28 },
            borderBottomRightRadius: { md: 28 },
          }}
        >
          {post?.feature_image && (
            <Box
              component="img"
              src={post.feature_image}
              alt={post.title}
              loading="eager"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scale(1.03)",
                filter: "saturate(1.05)",
              }}
            />
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: overlay,
            }}
          />

          <Container
            maxWidth="lg"
            sx={{ position: "relative", zIndex: 1, pb: 8 }}
          >
            <Stack spacing={3} alignItems="flex-start">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 999,
                  bgcolor: alpha("#FFFFFF", 0.12),
                  border: `1px solid ${alpha("#FFFFFF", 0.24)}`,
                  backdropFilter: "blur(10px)",
                  color: "common.white",
                }}
              >
                <Avatar
                  src={post?.primary_author?.profile_image || undefined}
                  alt={post?.primary_author?.name || "author"}
                  sx={{ width: 28, height: 28 }}
                />
                <Typography variant="body2">
                  {post?.primary_author?.name || "—"}
                </Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: alpha("#fff", 0.3) }}
                />
                <Typography variant="body2">
                  {post?.published_at
                    ? new Date(post.published_at).toLocaleDateString()
                    : "—"}{" "}
                  • {reading.minutes} min read
                </Typography>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.08,
                  fontSize: { xs: 36, sm: 48, md: 64 },
                  color: "transparent",
                  backgroundImage: `linear-gradient(29deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light}, ${theme.palette.secondary.dark})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  textShadow: post?.feature_image
                    ? "0 2px 16px rgba(0,0,0,.25)"
                    : "none",
                }}
              >
                {loading ? "…" : post?.title}
              </Typography>

              {/* Теги */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {post?.tags?.slice(0, 5).map((t) => (
                  <Chip
                    key={t.slug}
                    label={t.name}
                    color="secondary"
                    size="small"
                  />
                ))}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(-1)}
                  startIcon={<ArrowBackIosNewIcon />}
                >
                  Back
                </Button>
                <Tooltip title="Share">
                  <IconButton
                    onClick={() => {
                      if (navigator.share) {
                        navigator
                          .share({ title: post?.title, url: shareUrl })
                          .catch(() => {});
                      } else {
                        navigator.clipboard?.writeText(shareUrl);
                      }
                    }}
                    sx={{
                      color: "common.white",
                      bgcolor: alpha("#FFFFFF", 0.12),
                      "&:hover": { bgcolor: alpha("#FFFFFF", 0.18) },
                      border: `1px solid ${alpha("#FFFFFF", 0.24)}`,
                    }}
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
          {err && (
            <Typography color="error" sx={{ mb: 4 }}>
              {err}
            </Typography>
          )}

          <Box
            sx={{
              "& *": { maxWidth: "100%" },
              "& h1, & h2, & h3, & h4": {
                fontWeight: 800,
                mt: 4,
                mb: 2,
                scrollMarginTop: "96px",
              },
              "& h2": {
                color: "transparent",
                backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
              },
              "& p": {
                lineHeight: 1.8,
                fontSize: 18,
                mb: 2,
                color: "text.primary",
              },
              "& img": {
                borderRadius: 2,
                my: 3,
                display: "block",
                mx: "auto",
                boxShadow: "0 16px 48px rgba(0,0,0,.12)",
              },
              "& blockquote": {
                borderLeft: `4px solid ${alpha(
                  theme.palette.primary.main,
                  0.4
                )}`,
                pl: 2,
                color: "text.secondary",
                fontStyle: "italic",
                my: 3,
                background: alpha(theme.palette.primary.light, 0.04),
                borderRadius: 1,
              },
              "& pre, & code": {
                bgcolor: alpha(theme.palette.secondary.light, 0.7),
                borderRadius: 2,
                px: 1,
                py: 0.5,
              },
              "& ul, & ol": { pl: 3, my: 2 },
              "& a": {
                color: theme.palette.primary.main,
                textDecoration: "underline",
                textDecorationThickness: "2px",
                textUnderlineOffset: "3px",
              },
            }}
            dangerouslySetInnerHTML={{ __html: post?.html || "" }}
          />
        </Container>
      </Box>
    </>
  );
}
